import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, ShoppingCart, Ban, Wand2, Trash2 } from "lucide-react";
import ReasonPrompt from "@ui/ReasonPrompt";
import { isTaxable, itemCost, newShoppingItem, parseNoteItems } from "@domain/shopping/shoppingList";

const money = (n) =>
  `$${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const cellInput =
  "bg-transparent border border-transparent rounded px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-indigo-500 hover:border-gray-600";

/**
 * Owner-sourced parts for one Plan line. Once a line has any, its Materials
 * figure becomes their total; the same items surface on the Shopping List.
 *
 * Only lines with a Materials estimate (or existing items) get the full
 * panel and "Itemize from notes". Fee/service lines (permits, deposits,
 * consultants) just get a small opt-in link — itemizing their notes would
 * turn fees into taxed purchases and double-count them against Labor/Svcs.
 */
export default function PlanLineItems({ category, items, taxRate, setItems, manualEstimate = 0 }) {
  const [newName, setNewName] = useState("");
  const [droppingId, setDroppingId] = useState(null);
  const [optedIn, setOptedIn] = useState(false);

  const mine = items.filter((i) => i.categoryId === category.id && !i.droppedAt);
  const total = mine.reduce((s, i) => s + itemCost(i, taxRate), 0);
  const priced = mine.some((i) => i.unitPrice > 0);
  // Note items not yet on the list (any item, incl. dropped, counts as seen).
  const seen = new Set(items.filter((i) => i.categoryId === category.id).map((i) => i.name.trim().toLowerCase()));
  const isMaterialsLine = manualEstimate > 0 || mine.length > 0;
  const parsed = isMaterialsLine
    ? parseNoteItems(category.notes).filter((p) => !seen.has(p.name.toLowerCase()))
    : [];

  const update = (id, patch) =>
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));

  const add = () => {
    const name = newName.trim();
    if (!name) return;
    setItems((prev) => [...prev, newShoppingItem({ name, categoryId: category.id })]);
    setNewName("");
  };

  const itemizeFromNotes = () =>
    setItems((prev) => [
      ...prev,
      ...parsed.map((p) => newShoppingItem({ ...p, categoryId: category.id })),
    ]);

  const drop = (id, reason) => {
    update(id, { droppedAt: Date.now(), droppedReason: reason });
    setDroppingId(null);
  };

  // Remove outright — for entries added by mistake, as opposed to Drop,
  // which records why a planned purchase isn't needed.
  const remove = (item) => {
    if (window.confirm(`Remove "${item.name || "this item"}"? (Use Drop instead if you want to record why you're not buying it.)`)) {
      setItems((prev) => prev.filter((i) => i.id !== item.id));
    }
  };

  if (!isMaterialsLine && !optedIn) {
    return (
      <button
        onClick={() => setOptedIn(true)}
        className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-blue-300"
        title="For lines where you buy materials yourself — not for fees or services"
      >
        <ShoppingCart className="w-3.5 h-3.5" /> Itemize materials I'm buying
      </button>
    );
  }

  return (
    <div className="rounded-lg p-2.5" style={{ background: "var(--bg-row-hover)", border: "1px solid var(--border-row)" }}>
      <div className="flex items-center gap-2 mb-1.5">
        <ShoppingCart className="w-3.5 h-3.5 text-blue-400" />
        <p className="text-xs uppercase tracking-wider text-blue-300 font-semibold flex-1">
          Materials I'm buying{mine.length > 0 && ` (${mine.length}) · ${money(total)}`}
        </p>
        <Link to="/shopping" className="text-xs text-indigo-400 hover:text-indigo-300">
          Shopping List →
        </Link>
      </div>

      {mine.length === 0 && (
        <p className="text-xs text-gray-500 mb-1.5">
          List the parts you'll buy yourself. Once this line has any, its Materials figure becomes their total
          {taxRate > 0 ? ` (incl. ${taxRate}% tax + shipping)` : " (plus tax + shipping, set on the Shopping List)"}.
        </p>
      )}

      {mine.length > 0 && manualEstimate > 0 && (
        <p className="text-xs text-gray-500 mb-1.5">
          {priced
            ? <>Materials now uses these items: <span className="font-mono text-blue-300">{money(Math.round(total))}</span> vs. your earlier estimate of <span className="font-mono">{money(manualEstimate)}</span>.</>
            : <>Using your estimate of <span className="font-mono">{money(manualEstimate)}</span> until an item has a price.</>}
        </p>
      )}

      {parsed.length > 0 && (
        <button
          onClick={itemizeFromNotes}
          className="flex items-center gap-1.5 mb-1.5 px-2 py-1 text-xs font-medium rounded-md text-blue-300 bg-blue-500/10 hover:bg-blue-500/20"
          title={parsed.map((p) => `${p.quantity > 1 ? `${p.quantity}× ` : ""}${p.name} ${money(p.unitPrice)}`).join("\n")}
        >
          <Wand2 className="w-3.5 h-3.5" /> Itemize from notes ({parsed.length} item{parsed.length === 1 ? "" : "s"})
        </button>
      )}

      {mine.map((item) =>
        droppingId === item.id ? (
          <div key={item.id} className="py-1">
            <ReasonPrompt
              placeholder={`Why don't you need "${item.name || "this"}"?`}
              confirmLabel="Drop"
              onConfirm={(reason) => drop(item.id, reason)}
              onCancel={() => setDroppingId(null)}
            />
          </div>
        ) : (
          <div key={item.id} className="flex items-center gap-1.5 group/item">
            <input
              type="text"
              value={item.name}
              onChange={(e) => update(item.id, { name: e.target.value })}
              placeholder="Item"
              className={`flex-1 min-w-0 text-xs text-gray-300 placeholder-gray-600 ${cellInput}`}
            />
            {item.status === "purchased" && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 font-semibold">Purchased</span>
            )}
            <input
              type="number"
              min="0"
              step="0.01"
              value={item.unitPrice || ""}
              onChange={(e) => update(item.id, { unitPrice: parseFloat(e.target.value) || 0 })}
              placeholder="Price"
              className={`w-24 text-right text-xs font-mono text-gray-300 ${cellInput}`}
            />
            <span className="text-xs text-gray-600">×</span>
            <input
              type="number"
              min="0"
              step="1"
              value={item.quantity ?? ""}
              onChange={(e) => update(item.id, { quantity: parseFloat(e.target.value) || 0 })}
              className={`w-14 text-right text-xs font-mono text-gray-300 ${cellInput}`}
            />
            {taxRate > 0 && (
              <button
                onClick={() => update(item.id, { taxable: !isTaxable(item) })}
                className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                  isTaxable(item) ? "bg-blue-500/10 text-blue-300" : "bg-gray-500/15 text-gray-500 line-through"
                }`}
                title={isTaxable(item) ? `${taxRate}% sales tax applied — click if this isn't taxed` : "No sales tax — click to apply it"}
              >
                tax
              </button>
            )}
            <span className="w-24 text-right text-xs font-mono text-blue-300" title={isTaxable(item) ? "Incl. tax + shipping" : "No tax; incl. shipping"}>
              {money(itemCost(item, taxRate))}
            </span>
            <div className="flex items-center gap-1 md:opacity-0 md:group-hover/item:opacity-100 transition-opacity">
              <button
                onClick={() => setDroppingId(item.id)}
                className="p-0.5 text-gray-600 hover:text-amber-400"
                title="Drop — decided not to buy (records why)"
                aria-label="Drop item"
              >
                <Ban className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => remove(item)}
                className="p-0.5 text-gray-600 hover:text-red-400"
                title="Remove — added by mistake"
                aria-label="Remove item"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )
      )}

      <div className="flex items-center gap-1.5 mt-1">
        <Plus className="w-3.5 h-3.5 text-gray-600 flex-shrink-0" />
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") add(); }}
          placeholder="Add a part… press Enter"
          className="flex-1 text-xs bg-transparent focus:outline-none py-0.5 text-gray-300 placeholder-gray-600"
        />
      </div>
    </div>
  );
}
